import { Button } from '@heidi/design-system/button';
import { TypographyH4 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import * as m from 'motion/react-m';
import { memo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  EvidenceSBSStreamStatus,
  type EvidenceSBSCandidate,
} from '@heidi/evidence/contracts';
import type { SBSStreamState } from '@heidi/evidence/data';
import { EvidenceSBSSkipButton } from '../../side-by-side-evidence/components/EvidenceSBSSkipButton';
import { useEvidenceSBSComparison } from '../hooks/useEvidenceSBSComparison';
import {
  EvidenceSBSCandidateCard,
  type EvidenceSbsChatTimelineRenderer,
} from './EvidenceSBSCandidate';

interface Props {
  chatId: string;
  candidates: EvidenceSBSCandidate[];
  streamA: SBSStreamState;
  streamB: SBSStreamState;
  renderChatTimeline: EvidenceSbsChatTimelineRenderer;
  onConfirmed?: () => void;
  /**
   * EVI-1403 #4 + #5 (stream→chat handoff): receives the candidate id that
   * was actually confirmed, so the parent can find the correct stream
   * (streamA vs streamB) and copy its content into chatStateAtom before
   * resetSBS wipes everything. Previously this was a no-arg callback and the
   * parent tried to derive the chosen candidate from `candidates.find(c => c.confirmed)`,
   * which suffered a stale-closure bug: the callback prop here was captured
   * BEFORE the user clicked, so `candidates` still showed everyone with
   * `confirmed: false` when onSuccess fired — the parent silently bailed
   * out of syncChosenStreamToChat and the chat appeared blank until refresh.
   * Pass `undefined` only for the "both streams failed" auto-dismiss path
   * where there is no winning candidate to sync.
   */
  onMutationSuccess?: (chosenCandidateId?: string) => void;
  onSkip?: () => void;
  /**
   * EVI-1403 #2 (revised, no-rerender): when set, Comparison enters
   * minimal mode for the named candidate without remounting. The
   * unchosen card is omitted, the heading and sticky skip strip are
   * hidden, and the chosen card receives `minimal={true}` so its
   * Option header + Prefer button collapse out. The chosen card's
   * React fiber + DOM identity are preserved across the transition,
   * so its `ChatTimeline` + Streamdown subtree doesn't remount.
   *
   * The overlay sets this to the default candidate's id when the user
   * clicks Skip while streams are still active (skip-path minimal view).
   */
  minimalCandidateId?: string | null;
  /**
   * EVI-1680 (in-session polish): when true, the comparison renders ONE
   * candidate at a time with an "Option 1 / Option 2" toggle instead of the
   * full-width `md:grid-cols-2` side-by-side, which is far too cramped in the
   * narrow in-session panel. Both candidate cards stay mounted (the inactive
   * one is hidden via CSS) so their streams keep populating and toggling back
   * doesn't remount the timeline. Out-of-session keeps the side-by-side view.
   */
  inSession?: boolean;
  /**
   * EVI-1680 (in-session polish): background utility class for the overlay's
   * background-plane elements (currently the sticky Skip strip) so they match
   * the chat pane the overlay sits over. Defaults to `bg-surface` to preserve
   * the out-of-session look.
   */
  surfaceClassName?: string;
  createExperimentProps: (rawExperimentName: string) => {
    experiment_name: string;
    untyped_experiment_name?: string;
  };
}

function EvidenceSBSComparisonComponent({
  chatId,
  candidates,
  streamA,
  streamB,
  renderChatTimeline,
  onConfirmed,
  onMutationSuccess,
  onSkip,
  minimalCandidateId = null,
  inSession = false,
  surfaceClassName = 'bg-surface',
  createExperimentProps,
}: Props) {
  // EVI-1680 (in-session polish): which candidate the Option toggle is
  // showing. Only used in the in-session single-card layout; out-of-session
  // renders both side-by-side and ignores this.
  const [activeIndex, setActiveIndex] = useState(0);

  const { handleConfirm, handleSkip, isPending, allDoneOrErrored } =
    useEvidenceSBSComparison({
      chatId,
      candidates,
      streamA,
      streamB,
      onConfirmed,
      onMutationSuccess,
      onSkip,
      createExperimentProps,
    });

  if (candidates.length < 2) return null;

  // EVI-1403 #2 (revised, no-rerender): per-card minimal flags. When
  // `minimalCandidateId` is set we ALSO hide the unchosen card so the
  // chosen one takes over the column — but the chosen card's
  // EvidenceSBSCandidateCard instance (keyed by candidate_id) stays
  // mounted with `minimal={true}` instead of being replaced by a
  // separate minimal-view tree in the overlay.
  const isMinimalA =
    minimalCandidateId !== null && minimalCandidateId === candidates[0]!.candidate_id;
  const isMinimalB =
    minimalCandidateId !== null && minimalCandidateId === candidates[1]!.candidate_id;
  const showCardA = minimalCandidateId === null || isMinimalA;
  const showCardB = minimalCandidateId === null || isMinimalB;
  const isMinimalMode = minimalCandidateId !== null;

  // EVI-1680 (in-session polish): the narrow in-session panel shows ONE card at
  // a time. We keep using this single-column layout in BOTH phases — the
  // pre-pick Option toggle and the post-pick minimal view — so the chosen
  // card's fiber/DOM identity survives selection. Previously minimal mode fell
  // through to the out-of-session grid, which moved the chosen card into a
  // different parent hierarchy and remounted its ChatTimeline / Streamdown
  // subtree, making the answer visibly re-render/"reload" the instant you
  // picked an option (EVI-1680 follow-up).
  const useInSessionLayout = inSession;
  // The Option toggle (tab bar) is only offered before a pick; minimal mode
  // (post-pick / skip) collapses to the chosen card with no tabs.
  const showInSessionTabs = inSession && !isMinimalMode;
  // Which card the single-column in-session layout shows: the user's toggle
  // choice pre-pick, or the chosen (minimal) card afterwards.
  const inSessionVisibleIndex = isMinimalMode ? (isMinimalB ? 1 : 0) : activeIndex;

  const cardA = (
    <EvidenceSBSCandidateCard
      key={candidates[0]!.candidate_id}
      candidate={candidates[0]!}
      streamState={streamA}
      index={0}
      onConfirm={handleConfirm}
      // EVI-1766: "Prefer this response" is only available once BOTH streams
      // have settled — while either is still streaming the user may only Skip,
      // since they need the full answer before choosing A vs B. Also blocked by
      // a pending confirm mutation (avoid double-fire) or this side erroring
      // (nothing to confirm — the auto-fallback effect handles that case).
      confirmDisabled={
        isPending || !allDoneOrErrored || streamA.status === EvidenceSBSStreamStatus.ERROR
      }
      renderChatTimeline={renderChatTimeline}
      minimal={isMinimalA}
    />
  );

  const cardB = (
    <EvidenceSBSCandidateCard
      key={candidates[1]!.candidate_id}
      candidate={candidates[1]!}
      streamState={streamB}
      index={1}
      onConfirm={handleConfirm}
      confirmDisabled={
        isPending || !allDoneOrErrored || streamB.status === EvidenceSBSStreamStatus.ERROR
      }
      renderChatTimeline={renderChatTimeline}
      minimal={isMinimalB}
    />
  );

  return (
    <m.div
      // EVI-1403 #3 (expanded): take the full height of the overlay's inner
      // wrapper so the cards grid below can use `flex-1` to fill remaining
      // vertical space. Without `h-full`, the column collapses to its
      // content height and the cards' fixed `h-[65vh]` pushes the "Prefer
      // this response" buttons below the fold on shorter viewports.
      // EVI-1602: tightened from gap-6 to reduce the space between the
      // heading, the cards, and the Skip strip.
      className="flex size-full flex-col items-center gap-3"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, type: 'tween' }}
    >
      {!isMinimalMode && (
        <div className="flex shrink-0 flex-col items-center justify-center gap-1 text-center">
          <TypographyH4 className="text-text-primary">
            <FormattedMessage
              id="FZQDUa412Y"
              defaultMessage="Which output do you prefer?"
            />
          </TypographyH4>
        </div>
      )}

      {/*
        EVI-1680 (in-session polish): the narrow in-session panel can't fit a
        true side-by-side, so we offer an "Option 1 / Option 2" toggle and show
        one candidate at a time. Out-of-session never renders this.
      */}
      {showInSessionTabs && (
        <div
          role="tablist"
          aria-label="Compare responses"
          className="flex w-full shrink-0 items-center justify-center gap-2"
        >
          {candidates.slice(0, 2).map((candidate, i) => (
            <Button
              key={candidate.candidate_id}
              role="tab"
              aria-selected={activeIndex === i}
              variant={activeIndex === i ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setActiveIndex(i)}
            >
              <FormattedMessage
                id="NxWYm3JcW9"
                defaultMessage="Option {index}"
                values={{ index: i + 1 }}
              />
            </Button>
          ))}
        </div>
      )}

      {useInSessionLayout ? (
        /*
          EVI-1680: single-column layout used for BOTH the pre-pick Option
          toggle and the post-pick minimal view. Both cards stay mounted — the
          inactive/unchosen one is hidden via CSS, never unmounted — so the
          chosen card's ChatTimeline / Streamdown subtree is never remounted.
          This is what stops toggling OR selecting an option from visibly
          restarting/"reloading" the stream: the container structure is
          identical before and after a pick, so React preserves the card fiber
          (only its `minimal` prop flips). Out-of-session keeps the grid below.
        */
        <div className="flex min-h-0 w-full flex-1 flex-col">
          <div className={cn('h-full min-h-0', inSessionVisibleIndex !== 0 && 'hidden')}>
            {cardA}
          </div>
          <div className={cn('h-full min-h-0', inSessionVisibleIndex !== 1 && 'hidden')}>
            {cardB}
          </div>
        </div>
      ) : (
        /*
          EVI-1403 #3 (expanded): cards grid takes whatever vertical space is
          left after the heading + Skip strip (`flex-1 min-h-0`). Each card
          inside now uses `h-full` instead of a fixed `h-[65vh]`, so the
          Prefer button always sits at the bottom of the visible card area.

          Skip-path minimal mode: this same div switches to a single column
          (`grid-cols-1`, no `md:grid-cols-2`) and the unchosen card is omitted
          via the conditional below. The div itself stays mounted, so the chosen
          card's keyed React fiber is preserved across the transition.
        */
        <div
          className={cn(
            'grid min-h-0 w-full flex-1 grid-cols-1 gap-4',
            !isMinimalMode && 'md:grid-cols-2'
          )}
        >
          {showCardA && cardA}
          {showCardB && cardB}
        </div>
      )}

      {/*
        EVI-1403 #3: Skip Selection must stay visible at all times. Previously
        sat in normal flex flow (`shrink-0`), so on shorter viewports it fell
        below the fold and required scrolling — defeating the affordance.
        Now sticks to the bottom of the SBS overlay's scroll container
        (the `overflow-y-auto` parent in EvidenceSBSOverlay), with an opaque
        background so card content scrolling underneath is occluded cleanly.

        Hidden in skip-path minimal mode — once the user has skipped we no
        longer offer Skip again.

        EVI-1680: `surfaceClassName` matches the strip to the in-session
        container background (defaults to `bg-surface` out-of-session).
      */}
      {!isMinimalMode && (
        <div
          className={cn(
            'sticky bottom-0 -mx-4 flex w-[calc(100%+2rem)] justify-center border-t border-outline px-4 py-2',
            surfaceClassName
          )}
        >
          <EvidenceSBSSkipButton onSkip={handleSkip} disabled={isPending} />
        </div>
      )}
    </m.div>
  );
}

export const EvidenceSBSComparison = memo(EvidenceSBSComparisonComponent);
